/**
 * PATH: apps/qa/src/components/inspection/FloorBuilder/AIAnalysisCard.tsx
 * PURPOSE: AI suggestion card with apply action for FloorBuilder
 */

import { cn } from '@/lib/utils'
import { Sparkles, Loader2 } from 'lucide-react'
import type { AISuggestions } from './types'

interface AIAnalysisCardProps {
  isAnalyzing: boolean
  aiSuggestions: AISuggestions | null
  aiApplied: boolean
  onApply: () => void
}

export function AIAnalysisCard({ isAnalyzing, aiSuggestions, aiApplied, onApply }: AIAnalysisCardProps) {
  if (aiApplied || (!isAnalyzing && !aiSuggestions)) return null

  return (
    <div className={cn(
      'p-3 rounded-lg border animate-in fade-in duration-300',
      isAnalyzing
        ? 'bg-muted/50 border-muted'
        : aiSuggestions && aiSuggestions.confidence > 0.5
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : 'bg-amber-500/10 border-amber-500/30'
    )}>
      <div className="flex items-start gap-3">
        <div className={cn(
          'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
          isAnalyzing ? 'bg-muted' : 'bg-primary/10'
        )}>
          {isAnalyzing ? (
            <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4 text-primary" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-medium text-sm text-foreground">
              {isAnalyzing ? 'Analyzing...' : 'AI Suggestions'}
            </p>
            {aiSuggestions && !isAnalyzing && (
              <span className={cn(
                'text-[10px] px-1.5 py-0.5 rounded font-medium',
                aiSuggestions.confidence > 0.7
                  ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                  : aiSuggestions.confidence > 0.4
                    ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                    : 'bg-slate-500/20 text-slate-700 dark:text-slate-300'
              )}>
                {Math.round(aiSuggestions.confidence * 100)}% confident
              </span>
            )}
          </div>

          {aiSuggestions && !isAnalyzing && (
            <>
              <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{aiSuggestions.reasoning}</p>
              <p className="text-xs text-muted-foreground mb-3">
                Suggests: {aiSuggestions.floorCount} floors
                {aiSuggestions.includeBasement && ' + Basement'}
                {aiSuggestions.includeRooftop && ' + Rooftop'}
                {aiSuggestions.typicalAreas.length > 0 && ` • ${aiSuggestions.typicalAreas.slice(0, 3).join(', ')}${aiSuggestions.typicalAreas.length > 3 ? '...' : ''}`}
              </p>
              <button
                onClick={onApply}
                className="w-full py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 active:scale-[0.98] transition-all"
              >
                Apply AI Suggestions
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
